import { HeartHandshake, Waves, Sparkles, Globe2, ShieldCheck, GraduationCap, Award } from "lucide-react";
import Reveal from "@/components/Reveal";

const education = [
  { title: "MA, Psychology", detail: "Class of 2017 · Grade O (GPA 9.8)" },
  { title: "BA, Psychology", detail: "Undergraduate degree" },
  { title: "PG Diploma in Special Education", detail: "Asian College of Teachers" },
];

const certifications = [
  { title: "CPCAB Level 4 Diploma in Therapeutic Counselling", detail: "Core counselling qualification" },
  { title: "RE-CBT", detail: "Albert Ellis Institute, New York" },
  { title: "TA 101", detail: "Transactional Analysis" },
  { title: "Trauma-Informed Care", detail: "Specialist training" },
  { title: "Buddhist Practices & the Therapeutic Value of Expressive Arts & Movement", detail: "Soul Sphere, Pune" },
  { title: "Psychodrama in Psychotherapy", detail: "Veda Drama" },
];

const pillars = [
  {
    icon: HeartHandshake,
    title: "Relational & Psychodynamic",
    text: "Longer-term depth work, gently exploring the patterns, relationships and experiences that shape how you relate to yourself and others.",
  },
  {
    icon: Waves,
    title: "Somatic & Creative",
    text: "Close attention to what is communicated through the body — the sensations and experiences that words alone cannot express.",
  },
  {
    icon: Sparkles,
    title: "TA (101) & Psychodrama",
    text: "Training in Transactional Analysis and Psychodrama brings action, creativity and fresh insight to the therapeutic process.",
  },
  {
    icon: Globe2,
    title: "Cross-Cultural Identity",
    text: "A practice shaped by training in India and the UK — deeply mindful of culture, migration, belonging and difference.",
  },
  {
    icon: ShieldCheck,
    title: "Registered Member, NCPS",
    text: "Membership no. NCPS7249 — working within the NCPS ethical framework, with regular clinical supervision and ongoing professional development.",
    img: "/ncps-logo.png",
  },
];

const Approach = () => (
  <section id="approach" className="bg-[#EAE4DC]/60 py-20 md:py-32" data-testid="approach-section">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <Reveal>
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-[#8C827A]">Approach &amp; credentials</p>
        <h2 className="max-w-2xl font-serif text-3xl font-normal tracking-tight text-[#2C2825] sm:text-4xl lg:text-5xl">
          How I <span className="italic text-[#C46B4E]">work</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {pillars.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08} className={i === 3 ? "md:col-span-2" : ""}>
            <div
              data-testid={`pillar-${i + 1}`}
              className="group h-full rounded-2xl border border-[#E3DCD3] bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-25px_rgba(44,40,37,0.25)]"
            >
              {p.img ? (
                <img
                  src={p.img}
                  alt="NCPS Accredited Registrant — MNCPS (Acc.)"
                  className="mb-5 h-14 w-auto rounded-xl"
                  data-testid="pillar-ncps-logo"
                />
              ) : (
                <div className="mb-5 inline-flex rounded-full bg-[#E8EFE8] p-3">
                  <p.icon className="h-5 w-5 text-[#7A8B7B] transition-colors duration-300 group-hover:text-[#C46B4E]" strokeWidth={1.5} />
                </div>
              )}
              <h3 className="mb-3 font-serif text-xl font-medium text-[#3D3733]">{p.title}</h3>
              <p className="text-sm leading-relaxed text-[#5E564F]">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
        <Reveal delay={0.05}>
          <div className="h-full rounded-2xl border border-[#E3DCD3] bg-white p-8 sm:p-10" data-testid="education-card">
            <div className="mb-6 flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-[#C46B4E]" strokeWidth={1.5} />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8C827A]">Education</p>
            </div>
            <ul className="space-y-5">
              {education.map((e) => (
                <li key={e.title} className="border-l-2 border-[#E8EFE8] pl-5">
                  <p className="font-serif text-lg font-medium text-[#2C2825]">{e.title}</p>
                  <p className="mt-1 text-sm text-[#8C827A]">{e.detail}</p>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="h-full rounded-2xl border border-[#E3DCD3] bg-white p-8 sm:p-10" data-testid="certifications-card">
            <div className="mb-6 flex items-center gap-3">
              <Award className="h-5 w-5 text-[#C46B4E]" strokeWidth={1.5} />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8C827A]">Diplomas &amp; certifications</p>
            </div>
            <ul className="space-y-5">
              {certifications.map((c) => (
                <li key={c.title} className="border-l-2 border-[#E8EFE8] pl-5">
                  <p className="font-serif text-lg font-medium text-[#2C2825]">{c.title}</p>
                  <p className="mt-1 text-sm text-[#8C827A]">{c.detail}</p>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <p
          className="mx-auto mt-14 max-w-2xl text-center font-serif text-xl font-light italic leading-relaxed text-[#544D46] sm:text-2xl"
          data-testid="supervision-note"
        >
          &ldquo;I remain in my own ongoing therapy and supervision &mdash; the work I ask of my
          clients, I am committed to doing myself.&rdquo;
        </p>
      </Reveal>
    </div>
  </section>
);

export default Approach;
